"use client";
import { ReactNode, useState, useRef, useEffect } from "react";
import { useDarkMode, type ThemeMode } from "../hooks/useDarkMode";

const SunIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="5" />
    <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

const MoonIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const AutoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

interface DarkModeToggleProps {
  variant?: "simple" | "full";
  className?: string;
}

export function DarkModeToggle({
  variant = "simple",
  className = "",
}: DarkModeToggleProps) {
  const { themeMode, setThemeMode, toggleTheme, isDark } = useDarkMode();
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  if (variant === "simple") {
    // Simple toggle button for quick light/dark switching
    return (
      <button
        onClick={toggleTheme}
        className={`p-2 rounded-md border border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm focus:outline-none ${className}`}
        aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
        title={`Switch to ${isDark ? "light" : "dark"} mode`}
      >
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>
    );
  }

  // Full tri-state toggle with radio buttons
  const options: { mode: ThemeMode; label: string; icon: ReactNode }[] = [
    { mode: "light", label: "Light", icon: <SunIcon /> },
    { mode: "auto", label: "Auto", icon: <AutoIcon /> },
    { mode: "dark", label: "Dark", icon: <MoonIcon /> },
  ];

  const getActiveIndex = () => {
    return options.findIndex((option) => option.mode === themeMode);
  };

  const getIndicatorTransform = () => {
    const activeIndex = focusedIndex !== null ? focusedIndex : getActiveIndex();
    const translateX = activeIndex * 19; // Smaller spacing for tighter fit
    return `translateX(${translateX}px)`;
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center p-0.5 bg-theme-primary-foreground/5 rounded-full border border-gray-300 dark:border-gray-600 ${className} shadow-inner`}
    >
      {/* Animated selection indicator */}
      <div
        className="absolute rounded-full bg-yellow-50 dark:bg-slate-500 shadow transition-all duration-700 ease-out pointer-events-none w-4 h-4 px-0.5"
        style={{
          transform: getIndicatorTransform(),
          left: "2.5px",
          top: "2.5px",
        }}
      />

      {options.map(({ mode, label, icon }, index) => (
        <button
          key={mode}
          ref={(el) => (buttonRefs.current[index] = el)}
          onClick={() => setThemeMode(mode)}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex(null)}
          className={`
            relative px-0.5 transition-colors duration-200 rounded-full focus:outline-none z-10 w-4 h-4 flex justify-center items-center
            ${
              themeMode === mode
                ? "text-gray-900 dark:text-gray-100"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }
          `}
          aria-label={`Switch to ${label.toLowerCase()} mode`}
          title={`Switch to ${label.toLowerCase()} mode`}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

// Simple toggle hook for cases where you just need a toggle function
export function useSimpleDarkModeToggle() {
  const { toggleTheme, isDark } = useDarkMode();
  return { toggleTheme, isDark };
}
